import { ProductTypeEnum } from '@legacy/shared-types/enums/product-type.enum';
import { AffordabilityEvaluationResponse } from '@api/affordability/types';
import { ISelectOption } from '@components/common/Select/types';
import { Region } from '@hooks/useRegions/types';
import { URLSearchParamsInit } from 'react-router-dom';

export enum EmploymentTypesEnum {
  payeEmployed = 'payeEmployed',
  salariedDirector = 'salariedDirector',
  soleTrader = 'soleTrader',
  contractor = 'contractor',
  partnership = 'partnership',
  retired = 'retired',
  unemployed = 'unemployed',
}

export type FinanceDetails = AffordabilityCalculatorForm['financeDetails'];

export interface AffordabilityCalculatorForm {
  financeDetails: {
    financingAmount: number | null;
    deposit: number | null;
    ftv: number | null;
    product: null | string;
    numberOfApplicants: number | null;
    dependents: string | null;
    region: keyof typeof Region | null;
    term: {
      years: number | null;
      months: number | null;
    };
  };
  applicants: (Applicant | null)[];
  isApplicantsViewHidden: boolean;
  outgoings: {
    creditCommitments: {
      unsecuredLoanRepayments: number | null;
      unsecuredLoanRepaymentsPeriod: string | null;
      securedLoanRepayments: number | null;
      securedLoanRepaymentsPeriod: string | null;
      creditStoreCardsBalance: number | null;
    };
    expenditure: {
      childcare: number | null;
      childcarePeriod: string | null;
      travelExpenses: number | null;
      travelExpensesPeriod: string | null;
      maintenance: number | null;
      maintenancePeriod: string | null;
    };
  };
  results: AffordabilityEvaluationResponse[];
  isTechIssue?: boolean;
}

export interface BenefitsAndCredits {
  childBenefit: number | null;
  universalTaxCredits: number | null;
  stateDisabilityAllowance: number | null;
}

export interface OtherIncome {
  maintenanceIncome: number | null;
  maintenanceIncomePeriod: string | null;
  pensionIncome: number | null;
  pensionIncomePeriod: string | null;
  rentalIncome: number | null;
  rentalIncomePeriod: string | null;
}
export interface Applicant {
  isFilled: boolean;
  primaryIncome: Income;
  isSecondIncome: string;
  secondIncome: Income;
  otherIncome: OtherIncome;
  benefitsAndCredits: BenefitsAndCredits;
}

export type IncomeKeys = keyof Income;
export type MainIncomeType = 'primaryIncome' | 'secondIncome';

export enum MainTypesEnum {
  primaryIncome = 'primaryIncome',
  secondIncome = 'secondIncome',
}

export interface Income {
  basicAnnualSalary: number | null;
  basicAnnualSalaryPreviousYear: number | null;
  basicAnnualSalaryRecentYear: number | null;
  bonus: number | null;
  bonusPeriod: string | null;
  commission: number | null;
  commissionPeriod: string | null;
  dividendsPreviousYear: number | null;
  dividendsRecentYear: number | null;
  employmentType: EmploymentTypesEnum | null;
  guaranteedEmployeeBenefits: number | null;
  guaranteedEmployeeBenefitsPeriod: string | null;
  netTaxableIncomePreviousYear: number | null;
  netTaxableIncomeRecentYear: number | null;
  overtime: number | null;
  overtimePeriod: string | null;
  timeTrading: string | null;
  timeRemainingYears: number | null;
  timeRemainingMonths: number | null;
}

export interface IProductSelectOption extends ISelectOption {
  productTypeString: string;
  productType: ProductTypeEnum;
}

export enum CalculatorPages {
  financeDetails = 'financeDetails',
  applicantDetails = 'applicantDetails',
  outgoings = 'outgoings',
  reviewAnswers = 'reviewAnswers',
  results = 'results',
}

export enum CurrencyPeriodEnum {
  perYear = 'perYear',
  perMonth = 'perMonth',
}

export type SetSearchParamsType = (
  nextInit: URLSearchParamsInit,
  navigateOptions?:
    | {
        replace?: boolean | undefined;
        state?: any;
      }
    | undefined,
) => void;

export interface ApplicantDto {
  isRetiringWithinTenYears: boolean;
  projectedRetirementPayeIncome: number;
  isStandardResidency: boolean;
  primaryIncome: {
    timeInJob: number;
    basicIncome: number;
    employersAllowance: number;
    bonus: number;
    commission: number;
    overtime: number;
    timeTrading: number;
    salaryMostRecentYear: number;
    dividendsMostRecentYear: number;
    salaryPriorYear: number;
    dividendsPriorYear: number;
    netTaxableIncomeMostRecentYear: number;
    netTaxableIncomePriorYear: number;
    timeRemainingOnContract: number;
    employmentType: string;
    isHighRiskSector: boolean;
  };
  secondIncome: {
    timeInJob: number;
    basicIncome: number;
    employersAllowance: number;
    bonus: number;
    commission: number;
    overtime: number;
    timeTrading: number;
    salaryMostRecentYear: number;
    dividendsMostRecentYear: number;
    salaryPriorYear: number;
    dividendsPriorYear: number;
    netTaxableIncomeMostRecentYear: number;
    netTaxableIncomePriorYear: number;
    timeRemainingOnContract: number;
    employmentType: string;
    isHighRiskSector: boolean;
  };
  otherIncome: {
    pensionIncome: number;
    rentalIncome: number;
    maintenanceReceipts: number;
  };
}

export interface AffordabilityEvaluationDto {
  runBy?: string;
  affordabilitySpecificInputs: {
    monthsInitial: number | null;
    productType: ProductTypeEnum | null;
    termInMonths: number;
    financingAmount: number | null;
    isAddingProductFees: boolean;
  };
  creditCommitments: {
    creditCardBalance: number;
    unsecuredLoansMonthlyPaymentOnSpotDate: number;
    unsecuredLoansMonthlyPaymentOnRevertDate: number;
    securedLoansMonthlyPaymentOnSpotDate: number;
    securedLoansMonthlyPaymentOnRevertDate: number;
    buyNowPayLaterMonthlyPaymentOnSpotDate: number;
    buyNowPayLaterMonthlyPaymentOnRevertDate: number;
  };
  numberOfDependants13OrUnder: number;
  numberOfDependantsOver13: number;
  declaredExpenditure: {
    basicEssentialLiving: number;
    basicQualityOfLiving: number;
    travel: number;
    childcare: number;
    csaMaintenance: number;
    councilTax: number;
    serviceChargeGroundRent: number;
    other: number;
  };
  benefitsAndCredits: {
    workingTaxCredit: number;
    personalIndependencePayment: number;
    industrialInjuriesDisablementBenefit: number;
    childTaxCredit: number;
    childBenefit: number;
  };
  application: {
    purchaseRegion: Region;
    isRetiredHousehold: boolean;
    isJointApplication: boolean;
    isMarried: boolean;
    depositAmount: number;
    propertyValue: number;
    isDIPStage: boolean;
  };
  applicants: (ApplicantDto | null)[];
}
